/**
 * At Browser
 * This module is sniffing information about the browser
 *
 * Example:
 *  - Get the whole window.location object
 *      (window.)At.Browser.location();
 *
 *  - Get the current breakpoint
 *      (window.)At.Browser.breakpoint();
 *
 *  - Get the hash form the window.location object
 *      (window.)At.Browser.hash();
 *
 *  - Set the hash in the window.location object
 *      (window.)At.Browser.setHash({page:1, id:2});
 *
 *  - Merge hash in the window.location object
 *      (window.)At.Browser.mergeHash({page:'24'});
 *
 *  - Get the search form the window.location object
 *      (window.)At.Browser.search();
 *
 *  - Set the search in the window.location object
 *      (window.)At.Browser.setSearch({page:1, id:2});
 *
 *  - Merge search in the window.location object
 *      (window.)At.Browser.mergeSearch({page:'24'});
 *
 *  - Get the path from the window.location object
 *      (window.)At.Browser.path();
 *
 *  - Show module version
 *      (window.)At.Browser.version();
 *
 *  - Detect browser
 *      (window.)At.Browser.detect;
 *
 *  - Detect browser name
 *      (window.)At.Browser.detect.name;
 *
 *  - Detect browser version
 *      (window.)At.Browser.detect.version;
 *
 * Changelog
 *  - 1.0.0 delivered first version
 */
import {detect} from 'detect-browser';
import deparam from 'jquery-deparam/jquery-deparam';

let browser = detect();

/**
 * Check if window.At exists, if not make window.At as empty object
 * @type {{}}
 */
window.At = window.At || {};

/**
 * Attach module to the window object
 * @type {{location, breakpoint, hash, setHash, search, setSearch, path, version}}
 */
window.At.Browser = (() => {
    /**
     * Dependencies check for jQuery
     */
    if ($ === undefined) {
        throw '[at-browser] jQuery is required';
    }
    /**
     * Dependencies check for Browser
     */
    if (!browser) {
        // console.error('[at-browser] browser object is undefined or empty');
        /**
         * If unavailable set empty object
         * @type {{name: string, version: string}}
         */
        browser = {
            name: '',
            version: '',
        };
    }

    /**
     * Constant version
     */
    const _version = '1.0.0';

    /**
     * Get current window location
     * @private
     */
    const _locations = () => window.location;

    /**
     * Create a new object of the key part from the window.location object
     * @param key
     * @param split1
     * @param split2
     * @returns {{}}
     * @private
     */
    const _object = (key, prefix = '') => {
        /**
         * Get the location search property
         */
        let property = _locations()[key].substring(prefix.length + 1); // substring removes # from window.location.hash

        /**
         * Return the asked value, or return the object if function argument is empty
         */
        return deparam(property);
    };

    /**
     * Return pseudo property value
     *
     * @param pseudo
     * @param prop
     * @returns {*}
     */
    const _pseudoProperty = (pseudo, prop) => {
        const afterEl = window.getComputedStyle ? window.getComputedStyle(document.body, `:${pseudo}`) : false;

        if (!afterEl || !prop) return false;

        return afterEl.getPropertyValue(prop).replace(/^["']|["']$/g, '');
    };

    /**
     * Create a serialized representation of the given object
     * @param object
     * @private
     */
    const _serialize = (object) => $.param(object);

    /**
     * Returns the querystring part of the URL as object
     * @param key
     * @returns {{}}
     * @private
     */
    const _search = (key) => {
        /**
         * Get the location search object
         */
        const searchObject = _object('search');

        /**
         * Return key value, or return the object if key is empty
         */
        return (key !== undefined) ? searchObject[key] : searchObject;
    };

    /**
     * Set a new querystring in window.window.location object
     * @example At.Browser.setSearch({page:1,id:2});
     * @param object
     * @private
     */
    const _setSearch = (object) => window.location.search = _serialize(object);

    /**
     * Merge a new hash in window.window.location object
     * @example At.Browser.mergeSearch({page:24});
     * @param object
     * @returns {*}
     * @private
     */
    const _mergeSearch = (object) => {
        /**
         * Get the location hash object
         */
        const searchObject = _object('search');

        /**
         * Merge old hash object with new object
         */
        const merge = Object.assign(searchObject, object);

        /**
         * Return the new hash to the window.location object
         */
        return window.location.search = _serialize(merge);
    };

    /**
     * Returns the hash from window.window.location object
     * * @example At.Browser.hash();
     * @param key
     * @returns {{}}
     * @private
     */
    const _hash = (key, prefix = '') => {
        /**
         * Get the location hash object
         */
        const hashObject = _object('hash', prefix);

        /**
         * Return key value, or return the object if key is empty
         */
        return (key !== undefined) ? hashObject[key] : hashObject;
    };

    /**
     * Set a new hash in window.window.location object
     * @example At.Browser.setHash({page:1,id:2});
     * @param object
     * @private
     */
    const _setHash = (object, prefix = '') => window.location.hash = prefix + _serialize(object);

    /**
     * Merge a new hash in window.window.location object
     * @example At.Browser.mergeHash({page:24});
     * @param object
     * @returns {*}
     * @private
     */
    const _mergeHash = (object, prefix = '') => {
        return window.location.hash = _buildHash(object, true, prefix);
    };

    /**
     * Create a new hash-string
     *
     * @param object
     * @private
     */
    const _buildHash = (object, merge = true, prefix = '') => {

        if (!merge) {
            return prefix + _serialize(object);
        }

        /**
         * Get the location hash object
         */
        const hashObject = _object('hash', prefix);

        /**
         * Merge old hash object with new object
         */
        const merged = Object.assign(hashObject, object);

        /**
         * Return the new hash to the window.location object
         */
        return prefix + _serialize(merged);
    };

    /**
     * Returns the pathname of the URL as object
     * @param number
     * @returns {{}}
     * @private
     */
    const _path = (number) => {
        /**
         * Get the location hash object
         */
        const pathObject = _object('pathname');

        /**
         * Return key value, or return the object if key is empty
         */
        return (number !== undefined) ? pathObject[number] : pathObject;
    };

    // @NOTE Only works for chrome
    const _speed = () => {

        if (navigator.connection) {
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            const downloadSpeed = connection.downlink;

            if (downloadSpeed < 1) {
                return 'slow'
            } else if (downloadSpeed > 1 && downloadSpeed < 4) {
                return 'normal'
            }
            return 'fast'
        }

        return false
    };

    /**
     * Opposite of $.param. Give a string and get an object back
     * @param value
     * @returns {*}
     * @private
     */
    const _parseString = (value) => {
        return deparam(value);
    };

    /**
     *
     * Breakpoint, returns the current breakpoint
     * @returns {string|*}
     * @private
     */
    const _breakpoint = () => _pseudoProperty('after', 'content');

    /**
     *
     * Support touch
     * @returns {Boolean}
     * @private
     */
    const _supportsTouch = (('ontouchstart' in window) || (navigator.msMaxTouchPoints > 0));

    /**
     * Return object
     *
     * Get window.location object
     * @example At.Browser.location(); !
     * // returns {hash: "#twitter=true&facebook=true", search: "?at-photo-carousel=2&at-modal=AtPhotoCarousel", pathname: "/photo-carousel/item/01.html", port: "3000", hostname: "localhost"…}
     * @returns {Location|String}
     */
    return {
        location: _locations,
        breakpoint: _breakpoint,
        detect: {
            name: browser.name,
            version: browser.version
        },
        supportTouch: _supportsTouch,
        hash: _hash,
        setHash: _setHash,
        mergeHash: _mergeHash,
        buildHash: _buildHash,
        search: _search,
        setSearch: _setSearch,
        mergeSearch: _mergeSearch,
        parseString: _parseString,
        path: _path,
        speed: _speed,
        version: _version,
    };
})();

/**
 * Return current breakpoint to support old js modules and safety
 * @returns {*}
 */
window.breakpoint = () => window.At.Browser.breakpoint();
